import api from './api';

/**
 * Frontend Notification Service
 * Handles all notification-related API calls
 */
class NotificationService {
  /**
   * Get user notifications (last 5 by default)
   * @param {number} limit - Number of notifications to fetch
   * @returns {Promise<Array>} Notifications array
   */
  async getNotifications(limit = 5) {
    try {
      const response = await api.get(`/notifications?limit=${limit}`);
      return response.data.data || []; // Extract the notifications array from the response
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get all user notifications with pagination
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Paginated notifications
   */
  async getAllNotifications(page = 1, limit = 20) {
    try {
      const response = await api.get(`/notifications/all?page=${page}&limit=${limit}`);
      return response.data.data || { notifications: [], pagination: {} };
    } catch (error) {
      console.error('Error fetching all notifications:', error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise<number>} Unread count
   */
  async getUnreadCount() {
    try {
      const response = await api.get('/notifications/unread-count');
      return response.data.count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Response data
   */
  async markAsRead(notificationId) {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>} Response data
   */
  async markAllAsRead() {
    try {
      const response = await api.put('/notifications/read-all');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Create a test notification (for development)
   * @param {string} message - Notification message
   * @param {string} type - Notification type
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Created notification
   */
  async createTestNotification(message, type, metadata = {}) {
    try {
      const response = await api.post('/notifications/test', {
        message,
        type,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error creating test notification:', error);
      throw error;
    }
  }
}

export default new NotificationService();
